import { Component, ErrorInfo, ReactNode } from 'react';
import { MapPin } from 'lucide-react';
import i18next from 'i18next';

interface Props {
  children: ReactNode;
  /** Matches the height the map itself would have occupied, so the fallback
   *  doesn't collapse the surrounding layout. */
  height?: string;
}

interface State {
  hasError: boolean;
}

/**
 * Last line of defence around a Google map.
 *
 * Maps depend on a third-party script we don't control. When that script is
 * missing, blocked or stubbed, anything touching `google.maps` throws — and an
 * unguarded throw used to escape to the app-level boundary and blank the whole
 * screen (a Manager lost an entire inspection report that way: bug f8ff9d14,
 * "Can't find variable: google"). The map is always a secondary panel, never
 * the reason the page exists, so it degrades to a small notice instead.
 *
 * This is belt-and-braces: `getGoogleMapsApi()` is the real fix and no map code
 * should reference the bare `google` global any more. This just guarantees the
 * blast radius stays inside the map.
 */
export class MapErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Logged, not re-thrown: the app-level boundary must not see this.
    console.error('Map failed to render:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="w-full flex items-center justify-center gap-2 rounded-lg border border-line bg-subtle p-4 text-sm text-theme-secondary"
          style={this.props.height ? { height: this.props.height } : undefined}
        >
          <MapPin size={16} className="flex-shrink-0" />
          <span>
            {i18next.t('map.renderError', {
              ns: 'common',
              defaultValue: 'The map could not be displayed.',
            })}
          </span>
        </div>
      );
    }
    return this.props.children;
  }
}
